/**
 * LIRE LES INFORMATIONS D'UN COMPTEUR ENEDIS — une seule fois, pour les deux
 * entrées de la page « Données du compteur » (DEV-1508 lot 2).
 * ============================================================================
 *
 * INTENTION : ce qu'un client voit de son compteur (son segment, et surtout les
 * SENS qu'il porte) ne doit pas dépendre du chemin par lequel il est arrivé sur
 * la page. L'entrée dédiée et la page de données standard lisaient la même chose
 * de deux façons différentes ; l'une des deux se trompait.
 *
 * LE PIÈGE, précisément. Dans le store, une source est fusionnée
 * `{...sourceDapi, ...sourceLegacy}` : le `type` legacy, souvent null, ÉCRASE le
 * `type` calculé par dapi. Le getter générique `getInformationByType()` indexe
 * `informations` PAR CE `type`. Sur un compteur reconnu par son `idtype`
 * ("pdl") mais dont le `type` fusionné est null, il rend donc null, alors que
 * `informations.pdl` est bien servi. Conséquences servies au client :
 *
 *   • le segment retombe sur « INDET » alors qu'il est connu ;
 *   • les sens retombent sur le défaut « soutirage » seul, ce qui EFFACE
 *     l'injection d'un point qui injecte réellement. Une donnée juste, cachée,
 *     est une donnée fausse (E1).
 *
 * D'où la règle tenue ici : on lit `informations.pdl` À SA PLACE, jamais par le
 * `type` fusionné. Le getter générique du store reste bon pour ce qu'il fait ;
 * il n'est simplement pas le bon outil quand l'identité vient de `idtype`.
 */
import type { InformationPDL, VariableDataV2Servie } from "@types"
import type { SegmentEnedis } from "@modules/data/utils/ongletsEnedis"
import { DIRECTIONS } from "@modules/data/utils/ongletsEnedis"
import type { DirectionEnedis } from "@modules/data/services/sourceDataEnedis.service"

/** La forme minimale qu'on lit d'une source : juste son sac d'informations. */
type SourcePorteuseDInformations = {
    informations?: { pdl?: InformationPDL | null } | null
} | null

/**
 * Les informations du point, lues à leur place. `null` veut dire « aucune
 * information servie pour ce compteur », jamais « ce n'est pas un compteur ».
 */
export function lireInformationsPdl(
    source: SourcePorteuseDInformations | undefined
): InformationPDL | null {
    return source?.informations?.pdl ?? null
}

/**
 * Le segment du point. Absent se lit « je ne sais pas » (`INDET`), jamais un
 * segment par défaut : afficher C5 sur un point de segment inconnu ferait lire
 * au client des règles tarifaires qui ne sont pas les siennes.
 */
export function segmentDuPdl(
    informations: InformationPDL | null
): SegmentEnedis {
    return (informations?.compteur?.segment as SegmentEnedis) ?? "INDET"
}

/**
 * Les sens que ce compteur porte RÉELLEMENT (lot 5g).
 *
 * LE FAIT QUI TRANCHE EST `data.<sens>.variables_datav2`, et lui seul : c'est la
 * liste EXACTE des variables dataV2 servies pour ce sens. Une liste vide veut
 * dire « rien à montrer de ce côté », et un sens qui n'a rien à montrer n'est
 * pas un sens de ce compteur.
 *
 * CE QUI SE LISAIT AVANT, et le défaut servi : l'existence de l'OBJET
 * `data.soutirage` et celle de la clé `injection` du référentiel. Le P1 pur
 * `666069190b1d7fc6def335b4` (vérifié en prod le 17/09) porte les deux, sa
 * liste `soutirage` étant vide : la page ouvrait un sélecteur à trois positions,
 * se posait par défaut sur « Les deux » — donc sur du vide — et son en-tête
 * annonçait « Soutirage et injection » à un producteur qui ne soutire pas.
 *
 * Aucun sens servi rend `["soutirage"]` : la page se comporte alors exactement
 * comme sur le parc ordinaire, sans sélecteur, plutôt que de n'ouvrir aucun
 * onglet sur une lecture qui n'a peut-être pas encore abouti.
 *
 * Jamais une somme des deux sens : deux jeux de séries, « Les deux » est un
 * miroir (E5).
 */
export function directionsDuPdl(
    informations: InformationPDL | null
): DirectionEnedis[] {
    const servies = variablesServiesDuPdl(informations)
    const sens = DIRECTIONS.filter((d) => servies[d].length > 0)
    return sens.length > 0 ? sens : ["soutirage"]
}

/**
 * LES VARIABLES RÉELLEMENT SERVIES, PAR SENS (DEV-1508 lot 3, E1).
 *
 * C'est ce fait, et lui seul, qui décide des onglets et des séries de la page
 * (`ongletsEnedis.ts`). Un sens absent rend une liste vide : « la route ne l'a
 * pas servi » et « ce compteur n'a rien à ce sens » se ressemblent ici, et dans
 * les deux cas la page n'ouvre rien plutôt que d'inventer des noms.
 */
export function variablesServiesDuPdl(
    informations: InformationPDL | null
): Record<DirectionEnedis, VariableDataV2Servie[]> {
    return {
        soutirage: informations?.data?.soutirage?.variables_datav2 ?? [],
        injection: informations?.data?.injection?.variables_datav2 ?? [],
    }
}

/**
 * LE FUSEAU DE LA PAGE. Les bornes de disponibilité sont servies par dapi en
 * temps UNIVERSEL et SANS suffixe (`2025-11-06T23:00:00`) : c'est minuit à Paris
 * le 7 novembre, écrit en UTC. Un `String.slice(0, 10)` y lit « 06/11 », soit un
 * jour de trop au début du contexte — peint « Manquant » alors qu'aucune mesure
 * n'était due ce jour-là (défaut vu en prod le 15/09 sur le C5 réel : la bande
 * annonçait 312 / 314 dont le premier jour).
 */
const FUSEAU_DES_COMPTEURS = "Europe/Paris"

const JOUR_NU = /^\d{4}-\d{2}-\d{2}$/
/** Une borne SANS fuseau écrit : dapi sert de l'UTC nu, jamais de l'heure locale. */
const SANS_FUSEAU = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/

const JOUR_CIVIL = new Intl.DateTimeFormat("en-CA", {
    timeZone: FUSEAU_DES_COMPTEURS,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
})

/**
 * Un jour `YYYY-MM-DD` lu d'une borne servie, DANS LE FUSEAU DU COMPTEUR, ou
 * `null`.
 *
 * Un jour nu (`2025-11-07`) se rend tel quel : il n'a pas d'heure, donc pas de
 * fuseau, et lui en appliquer un le décalerait d'un jour une nuit sur deux.
 * Un horodatage sans suffixe est lu en UTC — jamais dans le fuseau du
 * NAVIGATEUR, qui n'est pas forcément celui du compteur.
 */
export function jourDeLaBorne(
    borne: Date | string | null | undefined
): string | null {
    if (!borne) return null
    if (borne instanceof Date) {
        return Number.isNaN(borne.getTime()) ? null : JOUR_CIVIL.format(borne)
    }
    const texte = String(borne)
    if (JOUR_NU.test(texte)) return texte
    const instant = new Date(SANS_FUSEAU.test(texte) ? `${texte}Z` : texte)
    if (Number.isNaN(instant.getTime())) {
        return JOUR_NU.test(texte.slice(0, 10)) ? texte.slice(0, 10) : null
    }
    return JOUR_CIVIL.format(instant)
}

/**
 * LA PREMIÈRE DONNÉE DÉTENUE, tous sens et tous grains confondus, ou `null`
 * quand aucune borne n'est servie.
 *
 * Deux familles de bornes, et le MINIMUM des deux :
 *  • `grains.*.date_from` de chaque variable servie ;
 *  • `data.<sens>.date_from`, la borne du SENS.
 *
 * ⚠️ Ce qui n'est PAS fait : préférer la borne du sens aux bornes de grain. Sur
 * le C4 témoin, lu en prod le 15/09, `data.soutirage.date_from` vaut
 * `2021-06-11` alors qu'une variable est détenue depuis `2019-11-25` : la
 * préférer amputerait le contexte de dix-huit mois de données réellement
 * détenues. Les deux se lisent, la plus ancienne gagne — c'est la CONVERSION en
 * jour civil, et elle seule, qui corrige le jour de trop.
 */
export function premiereDonneeDetenue(
    servies: Partial<Record<DirectionEnedis, VariableDataV2Servie[]>>,
    bornesDesSens: Partial<Record<DirectionEnedis, Date | string | null>> = {}
): string | null {
    let plusAncienne: string | null = null
    const retenir = (jour: string | null): void => {
        if (!jour) return
        if (plusAncienne === null || jour < plusAncienne) plusAncienne = jour
    }
    for (const entrees of Object.values(servies)) {
        for (const entree of entrees ?? []) {
            for (const bornes of Object.values(entree.grains ?? {})) {
                retenir(jourDeLaBorne(bornes?.date_from))
            }
        }
    }
    for (const borne of Object.values(bornesDesSens)) {
        retenir(jourDeLaBorne(borne))
    }
    return plusAncienne
}

/**
 * Les bornes `date_from` par SENS, lues à leur place dans les informations.
 * Un sens non servi ne pose pas de borne : absent n'est pas « rien avant ».
 */
export function bornesDesSensDuPdl(
    informations: InformationPDL | null
): Partial<Record<DirectionEnedis, Date | string | null>> {
    return {
        soutirage: informations?.data?.soutirage?.date_from ?? null,
        injection: informations?.data?.injection?.date_from ?? null,
    }
}

/**
 * LE CONTEXTE, BORNÉ PAR CE QUE LE COMPTEUR DÉTIENT VRAIMENT (lot 3).
 *
 * Un contexte qui commence avant la première donnée détenue peint des mois de
 * « manquant » sur une période où il n'y avait rien à collecter : le client lit
 * un défaut de collecte là où il n'y a qu'un compteur plus jeune que la fenêtre.
 * La borne servie par la route répond à la question sans une requête de plus.
 *
 * ⚠️ Ce qui n'est PAS fait, et ne doit pas l'être : raccourcir la FIN sur la
 * dernière donnée reçue. Un compteur arrêté depuis le 28/06 doit montrer son
 * arrêt, à sa date, dans la bande comme dans la courbe — le tronquer le ferait
 * disparaître (règle tenue depuis le lot 2, `PageSourceDonneesCompteur.vue`).
 */
export function contexteBornePar(
    contexte: { debut: string; fin: string },
    premiereDonnee: string | null
): { debut: string; fin: string } {
    if (!premiereDonnee) return contexte
    if (premiereDonnee <= contexte.debut) return contexte
    if (premiereDonnee > contexte.fin) return contexte
    return { debut: premiereDonnee, fin: contexte.fin }
}
